import { BalanceAdjustmentService } from 'src/app/angularJS/services/balance-adjustment/balance-adjustment.service';
import { DialogService } from '../../services/dialog/dialog.service';
import { ExternalRefundsService } from './../../services/external-refunds/external-refunds.service';
import { ExternalTopUpsService } from './../../services/external-top-ups/external-top-ups.service';
import { IBalanceAdjustmentRelatedItem } from 'src/app/interfaces/client-only/IBalanceAdjustmentRelatedItem';
import { PaymentService } from 'src/app/angularJS/services/payments/payments.service';
import { ResourceLinkType } from './../../enums/resourceLinkType.enum';
import { UserService } from './../../services/user/user.service';
import { NumberUtils } from './../../utils/number-utils';

export class ChargeAccountStatementTableController {
  public static $inject = [
    '$translate',
    'balanceAdjustmentService',
    '$rootScope',
    'paymentService',
    '$scope',
    'userService',
    'dialogService',
    'externalTopUpsService',
    'externalRefundsService'
  ];

  public adjustments: Array<InterACT.Interfaces.IChargeAccountStatementTableRowData>;
  public expandedItem: InterACT.Interfaces.ChargeAccounts.BalanceAdjustment;
  public paymentsAndRefundsForExpandedBalanceAdjustment: Array<IBalanceAdjustmentRelatedItem<InterACT.Interfaces.Payments.PaymentRequest | InterACT.Interfaces.Payments.PaymentRequestRefund>>;
  public externalShopTopUpsAndRefundsForExpandedBalanceAdjustment: Array<IBalanceAdjustmentRelatedItem<InterACT.Interfaces.ChargeAccounts.ExternalTopUp | InterACT.Interfaces.ChargeAccounts.ExternalRefund>>;
  public externalPartnerTopUpsAndRefundsForExpandedBalanceAdjustment: Array<IBalanceAdjustmentRelatedItem<InterACT.Interfaces.ChargeAccounts.ExternalTopUp | InterACT.Interfaces.ChargeAccounts.ExternalRefund>>;
  public paginatorData: InterACT.Interfaces.IPaginationData;

  private shopTopUpTranslation: string;
  private externalTopUpTranslation: string;
  private appTopUpTranslation: string;
  private autoTopUpTranslation: string;
  private settlementTranslation: string;
  private externalRefundTranslation: string;
  private bufferTranslation: string;
  private refundTranslation: string;
  private removeCreditTranslation: string;
  private unknownTranslation: string;
  private isShopTopUp: boolean;

  constructor(
    public $translate: ng.translate.ITranslateService,
    private balanceAdjustmentService: BalanceAdjustmentService,
    private $rootScope: InterACT.Interfaces.IActoraRootScope,
    private paymentService: PaymentService,
    private $scope,
    private userService: UserService,
    private dialogService: DialogService,
    private externalTopupService: ExternalTopUpsService,
    private externalRefundsService: ExternalRefundsService) {
    this.activate();
  }

  public async refundTopup(balanceAdjustment: InterACT.Interfaces.ChargeAccounts.BalanceAdjustment, totalRefundAmount: number): Promise<void> {
    const isPaymentRequest = this.checkLinkIsATypeOf([ResourceLinkType.PAYMENT_REQUEST], balanceAdjustment.resourceLink);

    const topupData = await this.dialogService.showRefundTopupDialog(balanceAdjustment, totalRefundAmount, isPaymentRequest);

    if (isPaymentRequest) {
      const paymentResource = balanceAdjustment.resourceLink.find(
        resource => resource.type
          ? resource.type === ResourceLinkType.PAYMENT_REQUEST
          : this.pluckOutResourceTypeFromLink(resource.link) === ResourceLinkType.PAYMENT_REQUEST
      );

      const paymentRequest = await this.paymentService.getPaymentRequest(this.pluckIdFromString(paymentResource.link), false) as InterACT.Interfaces.Payments.PaymentRequest;

      await this.dialogService
        .showRefundTopupConfirmDialog(topupData.amount, balanceAdjustment.amount, totalRefundAmount, paymentRequest.paymentToken.accountNumber)

      await this.paymentService.createPaymentRequestRefund({
        scheme: paymentRequest.scheme,
        amount: NumberUtils.round(topupData.amount),
        date: paymentRequest.date,
        customer: {
          link: paymentRequest.customer.link,
          name: paymentRequest.customer.name,
        },
        paymentRequest: {
          link: `/payment-requests/${paymentRequest.id}`
        }
      });

      await this.getAdjustments(this.paginatorData.currentPage, this.paginatorData.perPageCount)
    } else {
      await this.dialogService.showRefundTopupConfirmDialog(topupData.amount, balanceAdjustment.amount, totalRefundAmount);
      const newExternalRefund = await this.externalRefundsService.getNewExternalRefund(topupData, balanceAdjustment);
      await this.externalRefundsService.createExternalRefund(newExternalRefund);
      await this.getAdjustments(this.paginatorData.currentPage, this.paginatorData.perPageCount);
    }
  }

  public isExpandable(adjustment: InterACT.Interfaces.ChargeAccounts.BalanceAdjustment): boolean {
    if (!adjustment.resourceLink) {
      return false;
    }

    return adjustment.resourceLink.some(link => /^\/payment-requests\/.*$/.test(link.link)
      || /^\/external-top-ups\/.*$/.test(link.link)
      || /^\/external-refunds\/.*$/.test(link.link)
      || /^\/external-refunds\/.*$/.test(link.link)
      || /^\/payment-request-refunds\/.*$/.test(link.link)
    );
  }

  public toggleExpansion(adjustment: InterACT.Interfaces.ChargeAccounts.BalanceAdjustment): void {
    if (this.isExpandable(adjustment) && this.expandedItem !== adjustment) {
      this.expandedItem = adjustment;
      this.setExpandedItems(adjustment);
    } else {
      this.paymentsAndRefundsForExpandedBalanceAdjustment = [];
      this.externalShopTopUpsAndRefundsForExpandedBalanceAdjustment = [];
      this.externalPartnerTopUpsAndRefundsForExpandedBalanceAdjustment = [];
      this.expandedItem = undefined;
    }
  }

  private pluckOutResourceTypeFromLink(link: string) {
    const type = link.split('/')[1].replace('-', '_').toUpperCase();

    return type.substr(0, type.length - 1);
  }

  private checkLinkIsATypeOf(types: ResourceLinkType[], resourceLinks: InterACT.Interfaces.ChargeAccounts.BalanceAdjustmentResourceLink[]) {
    return types.some(type => resourceLinks.some(resourceLink => resourceLink.type ? resourceLink.type === type : this.pluckOutResourceTypeFromLink(resourceLink.link) === type));
  }

  private returnLinkOfASpecificType(type: ResourceLinkType, resourceLinks: InterACT.Interfaces.ChargeAccounts.BalanceAdjustmentResourceLink[]) {
    return resourceLinks.filter(link => link.type == type)[0].link;
  }

  private pluckIdFromString(link: string) {
    return link.split('/').pop();
  }

  private async canRefundTopup(adjustmentRowData: InterACT.Interfaces.IChargeAccountStatementTableRowData): Promise<void> {
    const refundAppTopupButtonPermissions = this.userService.permissions.ui['refund-app-topup'] ? this.userService.permissions.ui['refund-app-topup'] : false;
    const refundCashTopupButtonPermissions = this.userService.permissions.ui['refund-cash-topup'] ? this.userService.permissions.ui['refund-cash-topup'] : false;

    const isCashTopup = this.checkLinkIsATypeOf([ResourceLinkType.EXTERNAL_TOP_UP], adjustmentRowData.adjustment.resourceLink);
    const isPaymentRequest = this.checkLinkIsATypeOf([ResourceLinkType.PAYMENT_REQUEST], adjustmentRowData.adjustment.resourceLink);
    const hasRefundsAvailable = this.checkLinkIsATypeOf([ResourceLinkType.REFUND, ResourceLinkType.EXTERNAL_REFUND], adjustmentRowData.adjustment.resourceLink);

    const userCanRefund = (refundAppTopupButtonPermissions || refundCashTopupButtonPermissions) &&
      (
        (isPaymentRequest && refundAppTopupButtonPermissions.canAccess) ||
        (isCashTopup && refundCashTopupButtonPermissions.canAccess)
      )

    let refundRequests: any[] = [];

    if (hasRefundsAvailable) {
      for (const refundResource of adjustmentRowData.adjustment.resourceLink.filter(({ type }) => type === ResourceLinkType.EXTERNAL_REFUND || type === ResourceLinkType.REFUND)) {
        if (refundResource.type === ResourceLinkType.EXTERNAL_REFUND) {
          refundRequests.push(this.externalRefundsService.getExternalRefund(this.pluckIdFromString(refundResource.link)));
        } else {
          refundRequests.push(this.paymentService.getPaymentRequestRefund(this.pluckIdFromString(refundResource.link)));
        }
      }

      const externalRefundResponses = await Promise.all(refundRequests);
      const totalInRefunds = externalRefundResponses
        .map(refundResponse => {
          if (refundResponse.externalRefund) {
            return refundResponse.externalRefund.amount;
          }

          return refundResponse.paymentRequestRefund.amount;
        })
        .reduce((previousValue: number, currentValue: number) => previousValue + currentValue, 0);

      adjustmentRowData.totalRefundAmount = totalInRefunds;
      adjustmentRowData.showRefundOption = userCanRefund && totalInRefunds < adjustmentRowData.adjustment.amount;
    } else {
      adjustmentRowData.showRefundOption = userCanRefund;
    }
  }

  private initialisePaginatorData(): void {
    this.paginatorData = {
      currentPage: 1,
      perPageCount: 15,
      totalCount: 0
    }
  }

  private async getAdjustments(page: number, perPage: number): Promise<void> {
    perPage = perPage ? perPage : this.paginatorData.perPageCount;
    this.$rootScope.showSpinner = true;

    const response = await this.balanceAdjustmentService
      .getList(page, perPage, `chargeAccount.link='/charge-accounts/${this.$scope.vm.chargeAccountId}' sortBy created desc`);

    this.paginatorData.totalCount = response.meta.totalCount;
    this.adjustments = [];
    if ('latest' === this.$scope.vm.limitMode && response.balanceAdjustments.length > 0) {
      await this.addAdjustmentToList(response.balanceAdjustments[0]);
    } else {
      await Promise.all(response.balanceAdjustments.map(adjustment => this.addAdjustmentToList(adjustment)));
    }
    this.$rootScope.showSpinner = false;
  }

  private async addAdjustmentToList(adjustment: InterACT.Interfaces.ChargeAccounts.BalanceAdjustmentWithMeta): Promise<void> {

    this.adjustments.push({
      adjustment: adjustment.balanceAdjustment,
      created: adjustment.meta.created,
      type: await this.getAdjustmentType(adjustment.balanceAdjustment),
      valueSymbol: this.getValueSymbol(adjustment.balanceAdjustment),
      showRefundOption: false,
      totalRefundAmount: 0,
    });

    const latestAdjustment = this.adjustments[this.adjustments.length - 1];

    // Some adjustments will be added to the list later due to the async function taking longer to retrieve balance adjustments
    // that require additional requests to work out their origin (currently only external topups and refunds). This has meant that the
    // transactions appear out of order as previously this relied on the 'sortBy created desc' response to keep the list uniform. We now
    // need to ensure we are sorting the array by date for adjustment items that were added to the array out of date order.
    this.adjustments = this.adjustments.sort((objA, objB) => objB.created.localeCompare(objA.created));

    await this.canRefundTopup(latestAdjustment);
  }

  private async getAdjustmentType(adjustment: InterACT.Interfaces.ChargeAccounts.BalanceAdjustment): Promise<string> {
    let adjustmentType: string = this.unknownTranslation;

    if (adjustment.resourceLink) {
      if (this.checkLinkIsATypeOf([ResourceLinkType.EXTERNAL_TOP_UP], adjustment.resourceLink)) {
        const externalTopUpLink = this.returnLinkOfASpecificType(ResourceLinkType.EXTERNAL_TOP_UP, adjustment.resourceLink);
        const response = await this.externalTopupService.getExternalTopUp(this.pluckIdFromString(externalTopUpLink));
        adjustmentType = response.externalTopUp.shopName ? this.shopTopUpTranslation : this.externalTopUpTranslation;
      } else if (this.checkLinkIsATypeOf([ResourceLinkType.PAYMENT_REQUEST], adjustment.resourceLink) && adjustment.reason !== 'AUTO_TOPUP' && adjustment.reason !== 'BALANCE_TRANSFER' && adjustment.reason !== 'REFUND' && adjustment.reason !== 'SETTLEMENT') {
        adjustmentType = this.appTopUpTranslation;
      } else if (this.checkLinkIsATypeOf([ResourceLinkType.PAYMENT_REQUEST], adjustment.resourceLink) && adjustment.reason === 'AUTO_TOPUP') {
        adjustmentType = this.autoTopUpTranslation;
      } else if (this.checkLinkIsATypeOf([ResourceLinkType.EXTERNAL_REFUND], adjustment.resourceLink)) {
        adjustmentType = this.externalRefundTranslation;
      } else if (adjustment.reason === 'REFUND' && adjustment.resourceLink.length === 0) {
        adjustmentType = this.removeCreditTranslation;
      } else if (adjustment.reason === 'REFUND' && adjustment.resourceLink.length > 0) {
        adjustmentType = this.refundTranslation;
      } else if (adjustment.reason === 'TOP_UP') {
        adjustmentType = this.appTopUpTranslation;
      } else if (adjustment.reason === 'SETTLEMENT') {
        adjustmentType = this.settlementTranslation;
      } else if (this.checkLinkIsATypeOf([ResourceLinkType.PAYMENT_REQUEST], adjustment.resourceLink)) {
        const paymentResource = adjustment.resourceLink.find(
          resource => resource.type
            ? resource.type === ResourceLinkType.PAYMENT_REQUEST
            : this.pluckOutResourceTypeFromLink(resource.link) === ResourceLinkType.PAYMENT_REQUEST
        );

        const paymentRequest = await this.paymentService.getPaymentRequest(this.pluckIdFromString(paymentResource.link)) as InterACT.Interfaces.Payments.PaymentRequest;

        if (paymentRequest.paymentRequestItems && paymentRequest.paymentRequestItems.some(item =>
          'REFUNDABLE_BUFFER' === item.paymentItemType || 'NON_REFUNDABLE_BUFFER' === item.paymentItemType)) {
          adjustmentType = this.bufferTranslation;
        }

      }
    }

    return adjustmentType;
  }

  private getValueSymbol(adjustment: InterACT.Interfaces.ChargeAccounts.BalanceAdjustment) {
    let valueSymbol: string;

    if (adjustment.transactionType === 'INCREMENT') {
      valueSymbol = '+'
    } else if (adjustment.transactionType === 'DECREMENT' || adjustment.reason == 'SETTLEMENT') {
      valueSymbol = '–';
    } else {
      valueSymbol = '';
    }
    return valueSymbol;
  }

  private sortExpandedItems() {
    this.externalShopTopUpsAndRefundsForExpandedBalanceAdjustment.sort((a: any, b: any) => moment(b.created).valueOf() - moment(a.created).valueOf());
    this.paymentsAndRefundsForExpandedBalanceAdjustment.sort((a: any, b: any) => moment(b.created).valueOf() - moment(a.created).valueOf());
    this.externalPartnerTopUpsAndRefundsForExpandedBalanceAdjustment.sort((a: any, b: any) => moment(b.created).valueOf() - moment(a.created).valueOf());
  }

  private async setExpandedItems(adjustment: InterACT.Interfaces.ChargeAccounts.BalanceAdjustment): Promise<void> {
    this.paymentsAndRefundsForExpandedBalanceAdjustment = [];
    this.externalShopTopUpsAndRefundsForExpandedBalanceAdjustment = [];
    this.externalPartnerTopUpsAndRefundsForExpandedBalanceAdjustment = [];

    const paymentRequestsRegex = /^\/payment-requests\/.*$/;
    const externalTopupsRegex = /^\/external-top-ups\/.*$/;
    const externalRefundsRegex = /^\/external-refunds\/.*$/;
    const paymentRequestRefundRegex = /^\/payment-request-refunds\/.*$/;

    for (const resourceLink of adjustment.resourceLink) {
      const isPaymentRequest = paymentRequestsRegex.test(resourceLink.link);
      const isExternalTopUp = externalTopupsRegex.test(resourceLink.link);
      const isExternalRefund = externalRefundsRegex.test(resourceLink.link);
      const isPaymentRequestRefund = paymentRequestRefundRegex.test(resourceLink.link);

      if (isPaymentRequest) {
        const paymentRequestWithMeta = await this.paymentService.getPaymentRequest(this.pluckIdFromString(resourceLink.link), true) as InterACT.Interfaces.Payments.PaymentRequestResponse;

        this.paymentsAndRefundsForExpandedBalanceAdjustment.push({
          item: paymentRequestWithMeta.paymentRequest,
          createdBy: paymentRequestWithMeta.meta.createdBy.split(' (')[0],
          created: paymentRequestWithMeta.meta.created,
          type: 'PAYMENT_REQUEST',
        });

        this.sortExpandedItems();
      } else if (isExternalTopUp) {
        const response = await this.externalTopupService.getExternalTopUp(this.pluckIdFromString(resourceLink.link));

        if (response.externalTopUp.partnerReference) {
          this.externalPartnerTopUpsAndRefundsForExpandedBalanceAdjustment.push({
            item: response.externalTopUp,
            createdBy: response.meta.createdBy.split(' (')[0],
            created: response.meta.created,
            type: 'TOP_UP',
          });
        }
        // default to shop top-up expanded item
        else {
          this.externalShopTopUpsAndRefundsForExpandedBalanceAdjustment.push({
            item: response.externalTopUp,
            createdBy: response.meta.createdBy.split(' (')[0],
            created: response.meta.created,
            type: 'TOP_UP',
          });
          this.sortExpandedItems();
        }
      } else if (isExternalRefund) {
        this.externalRefundsService.getExternalRefund(this.pluckIdFromString(resourceLink.link)).then(response => {
          if (response.externalRefund.partnerReference) {
            this.externalPartnerTopUpsAndRefundsForExpandedBalanceAdjustment.push({
              item: response.externalRefund,
              createdBy: response.meta.createdBy.split(' (')[0],
              created: response.meta.created,
              type: 'EXTERNAL_REFUND',
            });
          }
          // default to shop refund expanded item
          else {
            this.externalShopTopUpsAndRefundsForExpandedBalanceAdjustment.push({
              item: response.externalRefund,
              createdBy: response.meta.createdBy.split(' (')[0],
              created: response.meta.created,
              type: 'EXTERNAL_REFUND',
            });
          }
          this.sortExpandedItems();
        });
      } else if (isPaymentRequestRefund) {
        this.paymentService.getPaymentRequestRefund(this.pluckIdFromString(resourceLink.link)).then(response => {
          this.paymentsAndRefundsForExpandedBalanceAdjustment.push({
            item: response.paymentRequestRefund,
            createdBy: response.meta.createdBy.split(' (')[0],
            created: response.meta.created,
            type: 'REFUND',
          });

          this.sortExpandedItems();
        });
      }
    }
  }

  private activate() {
    this.shopTopUpTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.SHOP_TOP_UP');
    this.externalTopUpTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.EXTERNAL_TOP_UP');
    this.appTopUpTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.APP_TOP_UP');
    this.autoTopUpTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.AUTO_TOP_UP');
    this.settlementTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.SETTLEMENT');
    this.externalRefundTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.REFUND');
    this.bufferTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.BUFFER');
    this.refundTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.REFUND');
    this.removeCreditTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.REMOVE_CREDIT');
    this.unknownTranslation = this.$translate.instant('BALANCE_ADJUSTMENT.TYPE.UNKNOWN');
    this.paymentsAndRefundsForExpandedBalanceAdjustment = [];
    this.externalShopTopUpsAndRefundsForExpandedBalanceAdjustment = [];
    this.externalPartnerTopUpsAndRefundsForExpandedBalanceAdjustment = [];
    this.initialisePaginatorData();
    this.getAdjustments(this.paginatorData.currentPage, this.paginatorData.perPageCount);
  }
}
